<script setup>
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.WEB.6 / META.7 — the inbox "Calling" tab (admins only):
// enable/disable calling on a WhatsApp Cloud number, the call button's
// visibility in WhatsApp, and call hours. Refused on coexistence numbers and
// on numbers Meta does not consider eligible. Saving changes the number's
// settings at Meta, so the copy says so.
//
// whatsapp-calling-ownership.UI.1 / LINE.1 / LINE.2 — "Who answers calls"
// (ring the assignee first, the designated person, ordered backups, ring
// seconds per person, what happens when nobody answers) and "Who can call
// customers" (the assignee, or anyone with access to the inbox). Calls cannot
// be turned on without a designated person.
//
// whatsapp-call-transcripts.REC.UI — with KAPTION_CALL_RECORDING, the
// "Recording" section (KaptionInboxRecordingSettings, saved on its own).
import { computed, onMounted, reactive, ref } from 'vue';
import Draggable from 'vuedraggable';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { DEFAULT_LINE } from 'dashboard/api/kaptionCalls';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useKaptionCallStore } from 'dashboard/components-next/kaption/calls/kaptionCallStore';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import KaptionInboxRecordingSettings from './KaptionInboxRecordingSettings.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();
const calls = useKaptionCallStore();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const showRecordingSettings = computed(() =>
  isKaptionFlagEnabled('KAPTION_CALL_RECORDING')
);
const recording = ref(null);
// NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.UI.1 — who answers: people or the AI.
const showAnswerMode = computed(() =>
  isKaptionFlagEnabled('KAPTION_CALL_ASSISTANT')
);
const assistant = ref(null);
const ANSWER_MODES = [
  {
    value: 'people_only',
    title: 'KAPTION_CALLS.SETTINGS.ANSWER_PEOPLE_TITLE',
    body: 'KAPTION_CALLS.SETTINGS.ANSWER_PEOPLE_BODY',
    ai: false,
  },
  {
    value: 'ai_first',
    title: 'KAPTION_CALLS.SETTINGS.ANSWER_AI_FIRST_TITLE',
    body: 'KAPTION_CALLS.SETTINGS.ANSWER_AI_FIRST_BODY',
    ai: true,
  },
  {
    value: 'ai_fallback',
    title: 'KAPTION_CALLS.SETTINGS.ANSWER_AI_FALLBACK_TITLE',
    body: 'KAPTION_CALLS.SETTINGS.ANSWER_AI_FALLBACK_BODY',
    ai: true,
  },
];

const WEEK_DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const isLoading = ref(true);
const isSaving = ref(false);
const meta = reactive({ eligible: true, coexistence: false });
const form = reactive({
  enabled: false,
  callIconVisibility: 'DEFAULT',
  hoursEnabled: false,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  weekly: WEEK_DAYS.map(day => ({
    day,
    open: !['SATURDAY', 'SUNDAY'].includes(day),
    from: '09:00',
    to: '18:00',
  })),
});

// whatsapp-calling-ownership.LINE.2
const RING_SECONDS = [15, 20, 30];
const line = reactive({ ...DEFAULT_LINE, backup_user_ids: [] });
const members = ref([]);
const showDesignatedPicker = ref(false);
const lineError = ref('');

const memberById = id => members.value.find(m => m.id === id) || null;
const designated = computed(() => memberById(line.designated_user_id));
const backups = computed({
  get: () =>
    line.backup_user_ids.map(
      id => memberById(id) || { id, name: `#${id}`, availability_status: '' }
    ),
  set: list => {
    line.backup_user_ids = list.map(member => member.id);
  },
});
const backupCandidates = computed(() =>
  members.value.filter(
    m =>
      m.id !== line.designated_user_id && !line.backup_user_ids.includes(m.id)
  )
);
const statusKey = member => {
  const status = member?.availability_status;
  if (status === 'online') return 'KAPTION_CALLS.LINE.STATUS_AVAILABLE';
  if (status === 'busy') return 'KAPTION_CALLS.LINE.STATUS_BUSY';
  return 'KAPTION_CALLS.LINE.STATUS_OFFLINE';
};
const statusClass = member =>
  member?.availability_status === 'online'
    ? 'text-n-teal-11'
    : 'text-n-slate-11';

const chooseDesignated = event => {
  const id = Number(event.target.value) || null;
  line.designated_user_id = id;
  line.backup_user_ids = line.backup_user_ids.filter(b => b !== id);
  showDesignatedPicker.value = false;
  lineError.value = '';
};
const addBackup = event => {
  const id = Number(event.target.value);
  if (id && !line.backup_user_ids.includes(id)) line.backup_user_ids.push(id);
  // eslint-disable-next-line no-param-reassign
  event.target.value = '';
};
const removeBackup = id => {
  line.backup_user_ids = line.backup_user_ids.filter(b => b !== id);
};
const moveBackup = (index, delta) => {
  const next = index + delta;
  if (next < 0 || next >= line.backup_user_ids.length) return;
  const list = [...line.backup_user_ids];
  [list[index], list[next]] = [list[next], list[index]];
  line.backup_user_ids = list;
};

const hydrateLine = (value = {}) => {
  Object.assign(line, DEFAULT_LINE, value, {
    backup_user_ids: [...(value.backup_user_ids || [])],
  });
};

const linePayload = () => ({
  designated_user_id: line.designated_user_id,
  backup_user_ids: [...line.backup_user_ids],
  ring_assignee_first: !!line.ring_assignee_first,
  ring_seconds_per_person: Number(line.ring_seconds_per_person),
  on_no_answer: line.on_no_answer,
  outbound_policy: line.outbound_policy,
  ...(showAnswerMode.value ? { answer_mode: line.answer_mode } : {}),
});

const OUTBOUND_POLICIES = [
  {
    value: 'assignee',
    title: 'KAPTION_CALLS.SETTINGS.POLICY_ASSIGNEE_TITLE',
    body: 'KAPTION_CALLS.SETTINGS.POLICY_ASSIGNEE_BODY',
  },
  {
    value: 'anyone_in_inbox',
    title: 'KAPTION_CALLS.SETTINGS.POLICY_ANYONE_TITLE',
    body: 'KAPTION_CALLS.SETTINGS.POLICY_ANYONE_BODY',
  },
];

const isCoexistence = computed(
  () => !!props.inbox?.is_coexistence || meta.coexistence
);
const isBlocked = computed(() => isCoexistence.value || !meta.eligible);

const hydrate = (settings = {}) => {
  meta.eligible = settings.eligible !== false;
  meta.coexistence = !!settings.coexistence;
  form.enabled = !!settings.enabled;
  form.callIconVisibility = settings.call_icon_visibility || 'DEFAULT';
  if (settings.line) hydrateLine(settings.line);
  if (settings.recording) recording.value = { ...settings.recording };
  assistant.value = settings.assistant || null;
  const hours = settings.call_hours || {};
  form.hoursEnabled = !!hours.enabled;
  if (hours.timezone) form.timezone = hours.timezone;
  if (Array.isArray(hours.weekly)) {
    form.weekly = WEEK_DAYS.map(day => {
      const row = hours.weekly.find(entry => entry.day === day);
      return row
        ? { day, open: true, from: row.open, to: row.close }
        : { day, open: false, from: '09:00', to: '18:00' };
    });
  }
};

const payload = () => ({
  line: linePayload(),
  enabled: form.enabled,
  call_icon_visibility: form.callIconVisibility,
  call_hours: {
    enabled: form.hoursEnabled,
    timezone: form.timezone,
    weekly: form.weekly
      .filter(row => row.open)
      .map(row => ({ day: row.day, open: row.from, close: row.to })),
  },
});

const loadMembers = async () => {
  try {
    const response = await calls.api().getInboxMembers?.(props.inbox.id);
    members.value = response?.data?.payload || [];
  } catch (e) {
    members.value = [];
  }
};

onMounted(async () => {
  try {
    const [{ data }] = await Promise.all([
      calls.api().getInboxCallingSettings(props.inbox.id),
      loadMembers(),
    ]);
    hydrate(data);
    calls.setInboxCalling(props.inbox.id, !!data?.enabled);
  } catch (e) {
    // a fresh inbox with no settings yet: keep the defaults
  } finally {
    isLoading.value = false;
  }
});

const save = async () => {
  // whatsapp-calling-ownership.LINE.1 — no calls without a designated person.
  if (form.enabled && !line.designated_user_id) {
    lineError.value = t('KAPTION_CALLS.SETTINGS.DESIGNATED_REQUIRED');
    return;
  }
  lineError.value = '';
  isSaving.value = true;
  try {
    const { data } = await calls
      .api()
      .updateInboxCallingSettings(props.inbox.id, payload());
    hydrate(data || payload());
    calls.setInboxCalling(props.inbox.id, form.enabled);
    useAlert(t('KAPTION_CALLS.SETTINGS.SAVED'));
  } catch (error) {
    const code = error?.response?.data?.error;
    if (code === 'designated_person_required')
      lineError.value = t('KAPTION_CALLS.SETTINGS.DESIGNATED_REQUIRED');
    useAlert(
      code === 'designated_person_required'
        ? t('KAPTION_CALLS.SETTINGS.DESIGNATED_REQUIRED')
        : code || t('KAPTION_CALLS.SETTINGS.SAVE_FAILED')
    );
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div
    data-testid="kaption-inbox-calling-settings"
    class="mx-8 my-4 flex max-w-2xl flex-col gap-4"
  >
    <!-- NOTICE: KAPTION-INTEGRATION - Warm Paper (warm-paper-redesign): the
         settings card already is the card, so each group is a linen inset
         with an eyebrow, never a card in a card; inputs sit on wells. -->
    <div>
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ $t('KAPTION_CALLS.SETTINGS.TITLE') }}
      </h3>
      <p class="mb-0 mt-0.5 text-sm text-n-slate-11">
        {{ $t('KAPTION_CALLS.SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <p
      v-if="!isAdmin"
      data-testid="kaption-calling-admin-only"
      class="text-sm text-n-slate-11"
    >
      {{ $t('KAPTION_CALLS.SETTINGS.ADMIN_ONLY') }}
    </p>

    <div v-else-if="isLoading" class="flex justify-center p-6"><Spinner /></div>

    <template v-else>
      <div
        v-if="isCoexistence"
        data-testid="kaption-calling-coexistence"
        class="flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-3 px-3 py-2.5 text-sm text-n-amber-11"
      >
        {{ $t('KAPTION_CALLS.SETTINGS.COEXISTENCE') }}
      </div>
      <div
        v-else-if="!meta.eligible"
        data-testid="kaption-calling-not-eligible"
        class="flex items-start gap-2 rounded-lg border border-n-amber-6 bg-n-amber-3 px-3 py-2.5 text-sm text-n-amber-11"
      >
        {{ $t('KAPTION_CALLS.SETTINGS.NOT_ELIGIBLE') }}
      </div>

      <fieldset
        :disabled="isBlocked"
        class="flex flex-col gap-5 rounded-lg border border-n-weak bg-n-background p-5"
        :class="{ 'opacity-60': isBlocked }"
      >
        <label class="flex items-center justify-between gap-4">
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('KAPTION_CALLS.SETTINGS.ENABLE') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('KAPTION_CALLS.SETTINGS.ENABLE_HINT') }}
            </span>
          </span>
          <Switch
            v-model="form.enabled"
            data-testid="kaption-calling-enabled"
          />
        </label>

        <label class="flex items-center justify-between gap-4">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('KAPTION_CALLS.SETTINGS.ICON_VISIBILITY') }}
          </span>
          <select
            v-model="form.callIconVisibility"
            class="!mb-0 h-9 w-40 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
          >
            <option value="DEFAULT">
              {{ $t('KAPTION_CALLS.SETTINGS.ICON_DEFAULT') }}
            </option>
            <option value="DISABLE_ALL">
              {{ $t('KAPTION_CALLS.SETTINGS.ICON_HIDDEN') }}
            </option>
          </select>
        </label>

        <div class="flex flex-col gap-3">
          <label class="flex items-center justify-between gap-4">
            <span class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('KAPTION_CALLS.SETTINGS.HOURS') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ $t('KAPTION_CALLS.SETTINGS.HOURS_HINT') }}
              </span>
            </span>
            <Switch
              v-model="form.hoursEnabled"
              data-testid="kaption-calling-hours-enabled"
            />
          </label>
          <template v-if="form.hoursEnabled">
            <label class="flex items-center justify-between gap-4 text-sm">
              <span class="text-n-slate-11">
                {{ $t('KAPTION_CALLS.SETTINGS.TIMEZONE') }}
              </span>
              <input
                v-model="form.timezone"
                type="text"
                class="!mb-0 h-9 w-56 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
              />
            </label>
            <div
              v-for="row in form.weekly"
              :key="row.day"
              data-testid="kaption-calling-hours-row"
              class="flex items-center gap-3 text-sm"
            >
              <label class="flex w-36 items-center gap-2 text-n-slate-12">
                <input v-model="row.open" type="checkbox" class="!mb-0" />
                {{ $t(`KAPTION_CALLS.SETTINGS.DAYS.${row.day}`) }}
              </label>
              <template v-if="row.open">
                <input
                  v-model="row.from"
                  type="time"
                  class="!mb-0 h-9 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-n-slate-12"
                />
                <span class="i-lucide-arrow-right size-3.5 text-n-slate-11" />
                <input
                  v-model="row.to"
                  type="time"
                  class="!mb-0 h-9 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-n-slate-12"
                />
              </template>
              <span v-else class="text-n-slate-11">
                {{ $t('KAPTION_CALLS.SETTINGS.CLOSED') }}
              </span>
            </div>
          </template>
        </div>
      </fieldset>

      <!-- whatsapp-calling-ownership.UI.1 — Who answers calls -->
      <section
        data-testid="kaption-calling-who-answers"
        class="flex flex-col gap-5 rounded-lg border border-n-weak bg-n-background p-5"
        :class="{ 'pointer-events-none opacity-60': isBlocked }"
      >
        <div>
          <h4
            class="mb-1.5 text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
          >
            {{ $t('KAPTION_CALLS.SETTINGS.WHO_ANSWERS') }}
          </h4>
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_CALLS.SETTINGS.WHO_ANSWERS_HINT') }}
          </p>
        </div>

        <!-- whatsapp-calling-ai.UI.1 — People only / AI first / AI when nobody can -->
        <div
          v-if="showAnswerMode"
          data-testid="kaption-calling-answer-mode"
          class="flex flex-col gap-2"
        >
          <label
            v-for="option in ANSWER_MODES"
            :key="option.value"
            :data-testid="`kaption-calling-answer-${option.value}`"
            class="flex items-start gap-3 rounded-lg border p-3"
            :class="[
              line.answer_mode === option.value
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-1',
              option.ai && !assistant
                ? 'cursor-not-allowed opacity-60'
                : 'cursor-pointer',
            ]"
          >
            <input
              v-model="line.answer_mode"
              type="radio"
              name="kaption-answer-mode"
              class="!mb-0 mt-0.5"
              :value="option.value"
              :disabled="option.ai && !assistant"
            />
            <span class="flex flex-col">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t(option.title) }}
              </span>
              <span class="text-xs text-n-slate-11">{{ $t(option.body) }}</span>
            </span>
          </label>
          <p
            data-testid="kaption-calling-assistant"
            class="flex items-center gap-1.5 text-xs text-n-slate-11"
          >
            <span
              class="i-lucide-sparkles size-3.5 text-[#7C3AED] dark:text-[#A78BFA]"
            />
            <template v-if="assistant">
              {{
                $t('KAPTION_CALLS.SETTINGS.ANSWER_ASSISTANT', {
                  name: assistant.name,
                })
              }}
            </template>
            <template v-else>
              {{ $t('KAPTION_CALLS.SETTINGS.ANSWER_NO_ASSISTANT') }}
            </template>
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <span
            class="text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
          >
            {{ $t('KAPTION_CALLS.SETTINGS.TIER_ASSIGNEE') }}
          </span>
          <label class="flex items-center justify-between gap-4">
            <span class="flex flex-col">
              <span class="text-sm text-n-slate-12">
                {{ $t('KAPTION_CALLS.SETTINGS.ASSIGNEE_FIRST') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ $t('KAPTION_CALLS.SETTINGS.ASSIGNEE_FIRST_HINT') }}
              </span>
            </span>
            <Switch
              v-model="line.ring_assignee_first"
              data-testid="kaption-calling-assignee-first"
            />
          </label>
        </div>

        <div class="flex flex-col gap-2">
          <span
            class="text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
          >
            {{ $t('KAPTION_CALLS.SETTINGS.TIER_DESIGNATED') }}
          </span>
          <div
            class="flex items-center gap-3 rounded-md border bg-n-solid-1 px-3 py-2"
            :class="lineError ? 'border-n-ruby-8' : 'border-n-weak'"
          >
            <template v-if="designated && !showDesignatedPicker">
              <Avatar
                :name="designated.name"
                :src="designated.thumbnail || designated.avatar_url"
                :size="28"
                rounded-full
              />
              <span class="flex min-w-0 flex-1 flex-col">
                <span
                  data-testid="kaption-calling-designated-name"
                  class="truncate text-sm text-n-slate-12"
                >
                  {{ designated.name }}
                </span>
                <span class="text-xs" :class="statusClass(designated)">
                  {{ $t(statusKey(designated)) }}
                </span>
              </span>
              <Button
                data-testid="kaption-calling-designated-change"
                :label="$t('KAPTION_CALLS.SETTINGS.CHANGE')"
                variant="link"
                size="sm"
                @click="showDesignatedPicker = true"
              />
            </template>
            <select
              v-else
              data-testid="kaption-calling-designated"
              class="!mb-0 h-9 flex-1 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
              :value="line.designated_user_id || ''"
              @change="chooseDesignated"
            >
              <option value="" disabled>
                {{ $t('KAPTION_CALLS.SETTINGS.DESIGNATED_PLACEHOLDER') }}
              </option>
              <option
                v-for="member in members"
                :key="member.id"
                :value="member.id"
              >
                {{ member.name }}
              </option>
            </select>
          </div>
          <p
            v-if="lineError"
            data-testid="kaption-calling-line-error"
            class="text-xs text-n-ruby-11"
          >
            {{ lineError }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ $t('KAPTION_CALLS.SETTINGS.DESIGNATED_HINT') }}
          </p>
        </div>

        <div class="flex flex-col gap-2">
          <span
            class="text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
          >
            {{ $t('KAPTION_CALLS.SETTINGS.TIER_BACKUPS') }}
          </span>
          <Draggable
            v-model="backups"
            item-key="id"
            handle=".kaption-backup-handle"
            tag="ol"
            class="flex flex-col gap-1.5"
          >
            <template #item="{ element: member, index }">
              <li
                data-testid="kaption-calling-backup"
                class="flex items-center gap-2 rounded-md border border-n-weak bg-n-solid-1 px-2 py-1.5"
              >
                <span
                  class="kaption-backup-handle i-lucide-grip-vertical size-4 cursor-grab text-n-slate-10"
                />
                <Avatar
                  :name="member.name"
                  :src="member.thumbnail || member.avatar_url"
                  :size="24"
                  rounded-full
                />
                <span class="flex min-w-0 flex-1 flex-col">
                  <span class="truncate text-sm text-n-slate-12">{{
                    member.name
                  }}</span>
                  <span class="text-xs" :class="statusClass(member)">
                    {{ $t(statusKey(member)) }}
                  </span>
                </span>
                <Button
                  :data-testid="`kaption-calling-backup-up-${member.id}`"
                  icon="i-lucide-arrow-up"
                  variant="ghost"
                  color="slate"
                  size="xs"
                  :disabled="index === 0"
                  :aria-label="$t('KAPTION_CALLS.SETTINGS.MOVE_UP')"
                  @click="moveBackup(index, -1)"
                />
                <Button
                  :data-testid="`kaption-calling-backup-remove-${member.id}`"
                  icon="i-lucide-x"
                  variant="ghost"
                  color="slate"
                  size="xs"
                  :aria-label="$t('KAPTION_CALLS.SETTINGS.REMOVE_BACKUP')"
                  @click="removeBackup(member.id)"
                />
              </li>
            </template>
          </Draggable>
          <select
            v-if="backupCandidates.length"
            data-testid="kaption-calling-add-backup"
            class="!mb-0 h-9 w-56 rounded-md border border-dashed border-n-weak bg-transparent !py-0 px-2 text-sm text-n-slate-11"
            value=""
            @change="addBackup"
          >
            <option value="" disabled>
              {{ $t('KAPTION_CALLS.SETTINGS.ADD_BACKUP') }}
            </option>
            <option
              v-for="member in backupCandidates"
              :key="member.id"
              :value="member.id"
            >
              {{ member.name }}
            </option>
          </select>
        </div>

        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-xs text-n-slate-11">
              {{ $t('KAPTION_CALLS.SETTINGS.RING_FOR') }}
            </span>
            <select
              v-model.number="line.ring_seconds_per_person"
              data-testid="kaption-calling-ring-seconds"
              class="!mb-0 h-9 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
            >
              <option v-for="n in RING_SECONDS" :key="n" :value="n">
                {{ $t('KAPTION_CALLS.SETTINGS.SECONDS', { n }) }}
              </option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-xs text-n-slate-11">
              {{ $t('KAPTION_CALLS.SETTINGS.NO_ANSWER') }}
            </span>
            <select
              v-model="line.on_no_answer"
              data-testid="kaption-calling-no-answer"
              class="!mb-0 h-9 rounded-md border border-n-weak bg-n-alpha-black2 !py-0 px-2 text-sm text-n-slate-12"
            >
              <option value="assign_designated">
                {{ $t('KAPTION_CALLS.SETTINGS.NO_ANSWER_ASSIGN') }}
              </option>
              <option value="leave_unassigned">
                {{ $t('KAPTION_CALLS.SETTINGS.NO_ANSWER_LEAVE') }}
              </option>
            </select>
          </label>
        </div>
      </section>

      <!-- whatsapp-calling-ownership.UI.1 / OUTBOUND — Who can call customers -->
      <section
        data-testid="kaption-calling-who-calls"
        class="flex flex-col gap-3 rounded-lg border border-n-weak bg-n-background p-5"
        :class="{ 'pointer-events-none opacity-60': isBlocked }"
      >
        <h4
          class="mb-0 text-[11px] font-medium uppercase leading-none tracking-[0.08em] text-n-slate-10"
        >
          {{ $t('KAPTION_CALLS.SETTINGS.WHO_CAN_CALL') }}
        </h4>
        <label
          v-for="option in OUTBOUND_POLICIES"
          :key="option.value"
          :data-testid="`kaption-calling-policy-${option.value}`"
          class="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
          :class="
            line.outbound_policy === option.value
              ? 'border-n-brand bg-n-brand/5'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-1'
          "
        >
          <input
            v-model="line.outbound_policy"
            type="radio"
            name="kaption-outbound-policy"
            class="!mb-0 mt-0.5"
            :value="option.value"
          />
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t(option.title) }}
            </span>
            <span class="text-xs text-n-slate-11">{{ $t(option.body) }}</span>
          </span>
        </label>
      </section>

      <div class="flex items-center justify-between gap-4">
        <p class="text-xs text-n-slate-11">
          {{ $t('KAPTION_CALLS.SETTINGS.META_NOTE') }}
        </p>
        <Button
          data-testid="kaption-calling-save"
          :label="$t('KAPTION_CALLS.SETTINGS.SAVE')"
          :is-loading="isSaving"
          :disabled="isBlocked"
          @click="save"
        />
      </div>

      <KaptionInboxRecordingSettings
        v-if="showRecordingSettings"
        :inbox="inbox"
        :initial="recording || undefined"
        :blocked="isBlocked"
      />
    </template>
  </div>
</template>
